import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormField, MatLabel, MatError, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { RootState } from '@next/core-lib/app';
import { ChipItem, ChipsFilterComponent } from '@next/core-lib/components';
import {
  AbstractDialog,
  AbstractDialogResultData,
  AbstractFormDialogData,
  FormDialogComponent,
} from '@next/core-lib/dialog';

import { AgrNumberDirective } from '@next/core-lib/form';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';
import { skipWhile, take, withLatestFrom } from 'rxjs/operators';

import { Barn, Silo } from '../../models/settings.model';

import { setSelectedBarn } from '../../store/actions/settings.actions';
import { getSelectedBarn, getSilos } from '../../store/selectors/settings.selectors';

import { barnNameValidator } from '../../validators/barn-name-validator';

export interface BarnItemDialogInputData extends AbstractFormDialogData {
  barnId?: number;
}
export interface BarnItemDialogResultData {
  barn: Barn;
}

@Component({
  selector: 'app-barn-item-dialog',
  templateUrl: './barn-item-dialog.component.html',
  styleUrls: ['./barn-item-dialog.component.scss'],
  imports: [
    FormDialogComponent,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    NgIf,
    MatError,
    AgrNumberDirective,
    MatSuffix,
    MatSelect,
    NgFor,
    MatOption,
    ChipsFilterComponent,
    MatIconButton,
    MatIcon,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class BarnItemDialogComponent extends AbstractDialog<BarnItemDialogResultData> {
  id: number;

  formGroup = new FormGroup({
    name: new FormControl('', [Validators.required], barnNameValidator(this.store)),
    surfaceArea: new FormControl<number>(undefined, [Validators.required]),
    silo: new FormControl<Silo[]>([]),
    newSection: new FormControl(''),
  });

  silos$ = this.store.select(getSilos);
  sections: ChipItem<string>[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: BarnItemDialogInputData,
    protected mdDialogRef: MatDialogRef<
      BarnItemDialogComponent,
      AbstractDialogResultData<BarnItemDialogResultData>
    >,
    private readonly store: Store<RootState>,
    private readonly localeService: LocaleService,
  ) {
    super();
    this.id = this.data.barnId;

    if (this.id && this.id > 0) {
      this.data.title = this.localeService.translate('settings.barn.edit-barn');

      this.store.dispatch(setSelectedBarn({ barnId: this.id }));
      this.store
        .select(getSelectedBarn)
        .pipe(
          withLatestFrom(this.silos$),
          skipWhile(([c, _]) => c == null),
          take(1),
        )
        .subscribe(([barn, silos]: [Barn, Silo[]]) => {
          const barnSilos: Silo[] = [];
          barn.properties?.silo?.forEach((barnSilo) => {
            const silo = silos.find((s) => s.id === barnSilo.id);
            if (silo) {
              barnSilos.push(silo);
            }
          });

          this.formGroup.controls.name.setValue(barn.name);
          this.formGroup.controls.surfaceArea.setValue(barn.surfaceArea);
          this.formGroup.controls.silo.setValue(barnSilos);

          if (!!barn.properties?.sections && Array.isArray(barn.properties?.sections)) {
            this.sections = barn.properties?.sections.map((section) => ({
              value: section,
              name: section,
            }));
          }
        });
    } else {
      this.data.title = this.localeService.translate('settings.barn.add-barn');
    }
  }

  addNewSection(event: Event) {
    event.preventDefault();
    if (this.formGroup.controls.newSection.value !== '') {
      this.sections = [
        ...this.sections,
        {
          value: this.formGroup.controls.newSection.value,
          name: this.formGroup.controls.newSection.value,
        } as ChipItem<string>,
      ];
      this.formGroup.controls.newSection.patchValue('');
    }
  }

  removeSection(section: ChipItem<string>) {
    this.sections = this.sections.filter((s) => s.value !== section.value);
  }

  save() {
    if (this.formGroup.valid) {
      const barn = {
        name: <string>this.formGroup.controls.name.value,
        surfaceArea: this.formGroup.controls.surfaceArea.value,
        properties: {
          silo: this.formGroup.controls.silo.value,
          sections: this.sections.map((section) => section.value),
        },
      } as Barn;

      if (this.id && this.id > 0) {
        barn.id = this.id;
      }

      this.confirm({
        barn,
      });
    }
  }
}
